import { useState, type ReactNode } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { FileDropZone } from '@agribot/polygon-checks-ui'
import type { UseMutationResult } from '@tanstack/react-query'

/** What every spreadsheet import answers: whether it was applied, rows read, and rows to fix. */
export interface ImportOutcome {
  applied: boolean
  rows: number
  issues: { row: number; message: string }[]
}

/**
 * A spreadsheet import in two steps: choosing a file checks it (a dry run)
 * and says what it would change or which rows to fix; Import applies it.
 */
export function ImportDialog<R extends ImportOutcome>({ title, help, run, summary, nothingToDo, onClose }: {
  title: string
  help: ReactNode
  run: UseMutationResult<R, Error, { file: File; dryRun: boolean }>
  /** What a result changes, in a line. */
  summary: (r: R) => string
  /** A checked file that would change nothing: Import stays off. */
  nothingToDo?: (r: R) => boolean
  onClose: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [checked, setChecked] = useState<R | null>(null)
  const [done, setDone] = useState<R | null>(null)
  const [rejected, setRejected] = useState<string | null>(null)
  const check = (f: File) => {
    setFile(f)
    setChecked(null)
    run.mutate({ file: f, dryRun: true }, { onSuccess: setChecked })
  }
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{help}</Typography>
        <FileDropZone
          compact
          accept=".csv,.xlsx"
          label={file ? `${file.name}: drop another file, or click to choose one` : 'Drop a CSV or Excel file here, or click to choose one'}
          inputLabel="Choose a file"
          busy={run.isPending}
          onRejected={(files) => setRejected(files[0]!.name)}
          onFiles={([f]) => { setRejected(null); check(f!) }}
        />
        {rejected && <Alert severity="warning">{rejected} is not a CSV or Excel file.</Alert>}
        {run.isPending && <Typography variant="body2">{checked ? 'Importing…' : 'Checking…'}</Typography>}
        {run.isError && <Alert severity="error">{run.error.message}</Alert>}
        {done && <Alert severity="success">Imported {done.rows} rows: {summary(done)}.</Alert>}
        {checked && !done && (
          checked.issues.length ? (
            <Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Box sx={{ fontWeight: 600, mb: 1 }}>{checked.issues.length} row{checked.issues.length === 1 ? '' : 's'} to fix first. Nothing has been imported.</Box>
              <Box component="ul" sx={{ m: 0, pl: 4, maxHeight: 240, overflow: 'auto' }}>
                {checked.issues.map((i, n) => <li key={n}>Row {i.row}: {i.message}</li>)}
              </Box>
            </Alert>
          ) : (
            <Alert severity="info">{checked.rows} rows checked: {summary(checked)}.</Alert>
          )
        )}
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
        <Button variant="text" onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button
            disabled={!file || !checked || checked.issues.length > 0 || run.isPending || Boolean(nothingToDo?.(checked))}
            onClick={() => file && run.mutate({ file, dryRun: false }, { onSuccess: setDone })}
          >
            Import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
